<template>
  <selectable-table-modal
    v-model:lists-selected="listsSelected"
    :show-modal="showModal"
    :handle-deselect-all="handleDeselectAll"
    :columns="table.columns"
    :rows="table.rows"
    :table-loading="areExistingListsLoading"
    header-title="ADD CONFIGURATION(S) TO..."
    :on-handle-confirm="onHandleConfirm"
    :disable-confirm="listsSelected === 0"
    :fetch-items="fetchExistingLists"
    :toggle-mine-only="toggleMineOnly"
    :filters="filters"
    :has-filters="true"
    :pagination="pagination"
    :handle-pagination-change="handlePaginationChange"
    @update:show-modal="closeModal"
  >
  </selectable-table-modal>
</template>

<script>
  import { onBeforeMount, reactive, ref, watch } from 'vue'
  import { getConfigurationLists, updateConfigurationsList } from '@/api'
  import notify from '@/utils/notify'
  import { useTable } from '@/composables/useTable'
  import { ADD_NEW_CONFIGS_TO_SAVED_LISTS_TABLE_COLUMNS } from '@/components/pac/saved-lists/utils'
  import { PAC_SAVED_LISTS_ROUTE } from '@/router/routeNames'
  import { useRouter } from 'vue-router'
  import SelectableTableModal from '@/components/pac/saved-lists/SelectableTableModal.vue'
  import { useStore } from 'vuex'
  import { usePagination } from '@/composables/usePagination'

  export default {
    name: 'AddConfigurationsToListModal',
    components: { SelectableTableModal },
    props: {
      showModal: {
        type: Boolean,
        default: false
      },
      selectedConfigurationsIdsSet: {
        type: Object,
        required: true,
        default: () => {}
      },
      handleDeselectAll: {
        type: Function,
        required: true,
        default: () => ({})
      }
    },
    emits: ['update:show-modal'],
    setup(props, { emit }) {
      const loadingSave = ref(false)
      const configurationsListName = ref('')
      const nameField = ref(null)
      const { pagination, handleChange: handlePaginationChange } = usePagination(
        { pageSize: 15, maxNavPage: 3 }
      )
      const { table, callbacks: tableCallbacks } = useTable(ADD_NEW_CONFIGS_TO_SAVED_LISTS_TABLE_COLUMNS)
      const areExistingListsLoading = ref(false)

      const listsSelected = ref([])

      const router = useRouter()

      const closeModal = () => {
        emit('update:show-modal')
      }

      const deselectAllLists = () => {
        listsSelected.value = []
      }

      const store = useStore()
      const filters = reactive({
        mineOnly: false,
        searchField: ''
      })

      const fetchExistingLists = async (searchField = '') => {
        areExistingListsLoading.value = true
        const params = {
          page: pagination.currentPage - 1,
          size: pagination.pageSize,
          sortBy: 'modifiedDate',
          sortDirection: 'DESC'
        }
        if (searchField !== '') params.search = searchField
        if (filters.mineOnly) params.creator = store.state.user.username

        const fetchExistingListsPromise = () => getConfigurationLists(params)
        const response = await tableCallbacks.fetchTableData(fetchExistingListsPromise)
        pagination.totalPages = response.totalPages || 1
        pagination.totalElements = response.totalElements || 0
        areExistingListsLoading.value = false
      }

      const toggleMineOnly = () => {
        filters.mineOnly = !filters.mineOnly
        if (pagination.currentPage > 1) {
          pagination.currentPage = 1
        }
        else {
          fetchExistingLists()
        }
      }

      const onHandleConfirm = async selectedListIds => {
        try {
          loadingSave.value = true
          const savedLists = await getConfigurationLists()

          await Promise.all(
            selectedListIds.map(async listId => {
              const savedList = savedLists.elements.find(list => list.id === listId)

              const configurationIds = [
                ...new Set([
                  ...(savedList?.configurationIds ?? []),
                  ...props.selectedConfigurationsIdsSet
                ])
              ]

              await updateConfigurationsList({
                listId,
                configurationIds
              })
            })
          )

          const savedListsRouteHref = router.resolve({
            name: PAC_SAVED_LISTS_ROUTE.name
          }).href

          notify({
            title: 'SUCCESS',
            content: `
              <div>
                <div>The configurations(s) were added to your selected list(s)</div>
                <div style="margin-top: 8px;">
                  <a href="${ savedListsRouteHref }"
                style="color: #ffffff; text-decoration: underline; font-weight: 500;">
                  Go to Saved Lists
                  </a>
                </div>
              </div>
            `,
            type: 'positive',
            html: true
          })

          props.handleDeselectAll(true)
          deselectAllLists()
          closeModal()
        }
        catch (err) {
          console.error(err)
        }
        finally {
          loadingSave.value = false
        }
      }

      onBeforeMount(async () => {
        await fetchExistingLists()
      })

      watch(() => pagination.currentPage, () => {
        fetchExistingLists()
      })

      return {
        onHandleConfirm,
        loadingSave,
        configurationsListName,
        nameField,
        closeModal,
        areExistingListsLoading,
        table,
        fetchExistingLists,
        listsSelected,
        toggleMineOnly,
        filters,
        handlePaginationChange,
        pagination
      }
    }
  }
</script>

<style lang="scss">
  .add-configurations-to-list-modal {
    width: 100%;
    max-height: calc(100vh - 250px);

    &__pagination {
      display: flex;
      justify-content: center;
      padding-top: 12px;
    }
  }
</style>
